import { useEffect, useId, useRef, useState } from 'react'

export function vitalNumber(value) {
  if (value == null || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

export function axisStamp(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return { date: '', time: '' }
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return { date: `${day}/${month}`, time: `${hours}:${minutes}` }
}

function labelIndexes(count) {
  if (count <= 6) return Array.from({ length: count }, (_, index) => index)
  const step = Math.ceil(count / 4)
  const indexes = []
  for (let index = 0; index < count; index += step) indexes.push(index)
  if (indexes[indexes.length - 1] !== count - 1) indexes.push(count - 1)
  return indexes
}

function plotFrame(size) {
  const width = Math.max(280, size.width || 480)
  const height = Math.max(150, size.height || 216)
  const padLeft = 42
  const padRight = 16
  const padTop = 16
  const padBottom = 34
  return {
    width,
    height,
    padLeft,
    padRight,
    padTop,
    padBottom,
    plotWidth: width - padLeft - padRight,
    plotHeight: height - padTop - padBottom
  }
}

function badgeBox(x, y, label) {
  const width = Math.max(26, label.length * 5.2 + 8)
  const height = 13
  const above = y - 16
  const top = above >= 2 ? above : y + 8
  return { x: x - width / 2, y: top, width, height, textY: top + 9.5 }
}

function linePath(coords) {
  let path = ''
  let drawing = false
  coords.forEach((point) => {
    if (point.y == null) {
      drawing = false
      return
    }
    path += `${drawing ? 'L' : 'M'}${point.x.toFixed(1)} ${point.y.toFixed(1)} `
    drawing = true
  })
  return path.trim()
}

export function SeriesChart({
  points,
  secondary = [],
  yMin,
  yMax,
  ticks,
  tickLabel,
  guides = [],
  band = null,
  gradientFrom,
  gradientTo,
  secondaryStroke = '#6366f1',
  showArea = true,
  ariaLabel
}) {
  const gradientId = useId().replace(/:/g, '')
  const boxRef = useRef(null)
  const [box, setBox] = useState({ width: 0, height: 0 })
  useEffect(() => {
    const node = boxRef.current
    if (!node) return undefined
    const measure = () => {
      const next = {
        width: Math.round(node.clientWidth),
        height: Math.round(node.clientHeight)
      }
      setBox((current) => (
        current.width === next.width && current.height === next.height ? current : next
      ))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  const frame = plotFrame(box)
  const { width, height, padLeft, padRight, padTop, plotWidth, plotHeight } = frame
  const yOf = (value) => padTop + ((yMax - value) / (yMax - yMin || 1)) * plotHeight
  const xOf = (index) => {
    if (points.length <= 1) return padLeft + plotWidth / 2
    return padLeft + (index / (points.length - 1)) * plotWidth
  }
  const coords = points.map((point, index) => ({
    x: xOf(index),
    y: Number.isFinite(point.value) ? yOf(point.value) : null,
    at: point.at,
    label: point.label
  }))
  const secondaryCoords = secondary.map((point, index) => ({
    x: xOf(index),
    y: Number.isFinite(point.value) ? yOf(point.value) : null
  }))
  const line = linePath(coords)
  const secondaryLine = linePath(secondaryCoords)
  const first = coords.find((point) => point.y != null)
  const last = [...coords].reverse().find((point) => point.y != null)
  const area = showArea && first && last
    ? `${line} L ${last.x.toFixed(1)} ${(padTop + plotHeight).toFixed(1)} L ${first.x.toFixed(1)} ${(padTop + plotHeight).toFixed(1)} Z`
    : ''
  const labels = new Set(labelIndexes(coords.length))

  return (
    <div className="vital-plot" ref={boxRef}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label={ariaLabel}>
        <defs>
          <linearGradient id={`${gradientId}-line`} x1="0" y1={padTop} x2="0" y2={padTop + plotHeight} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={gradientFrom} />
            <stop offset="100%" stopColor={gradientTo} />
          </linearGradient>
          <linearGradient id={`${gradientId}-area`} x1="0" y1={padTop} x2="0" y2={padTop + plotHeight} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={gradientFrom} stopOpacity="0.28" />
            <stop offset="100%" stopColor={gradientTo} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {band && (
          <rect
            x={padLeft}
            y={yOf(band.to)}
            width={plotWidth}
            height={Math.max(0, yOf(band.from) - yOf(band.to))}
            className="vital-band"
          />
        )}
        {ticks.map((tick) => {
          const y = yOf(tick)
          return (
            <g key={tick}>
              <line x1={padLeft} x2={width - padRight} y1={y} y2={y} className="vital-grid" />
              <text x={padLeft - 6} y={y + 3} textAnchor="end" className="vital-axis">
                {tickLabel(tick)}
              </text>
            </g>
          )
        })}
        {guides.map((guide) => (
          <line
            key={guide.key}
            x1={padLeft}
            x2={width - padRight}
            y1={yOf(guide.value)}
            y2={yOf(guide.value)}
            className={guide.className}
          />
        ))}
        {area && <path d={area} fill={`url(#${gradientId}-area)`} />}
        {secondaryLine && (
          <path d={secondaryLine} fill="none" stroke={secondaryStroke} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        )}
        {line && (
          <path d={line} fill="none" stroke={`url(#${gradientId}-line)`} strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />
        )}
        {secondaryCoords.map((point, index) => (
          point.y == null ? null : <circle key={`sec-${index}`} cx={point.x} cy={point.y} r="4" className="vital-point vital-point-secondary" />
        ))}
        {coords.map((point, index) => {
          if (point.y == null) return null
          const stamp = axisStamp(point.at)
          return (
            <g key={`${point.at}-${index}`}>
              <circle cx={point.x} cy={point.y} r="4.5" className="vital-point" />
              {labels.has(index) && (
                <text x={point.x} y={height - 16} textAnchor="middle" className="vital-axis">
                  <tspan x={point.x} dy="0">{stamp.date}</tspan>
                  <tspan x={point.x} dy="10">{stamp.time}</tspan>
                </text>
              )}
            </g>
          )
        })}
        {coords.map((point, index) => {
          if (!point.label) return null
          const anchor = point.y ?? secondaryCoords[index]?.y
          if (anchor == null) return null
          const badge = badgeBox(point.x, anchor, point.label)
          return (
            <g key={`value-${point.at}-${index}`}>
              <rect x={badge.x} y={badge.y} width={badge.width} height={badge.height} rx="3" className="vital-value-box" />
              <text x={point.x} y={badge.textY} textAnchor="middle" className="vital-value">{point.label}</text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

export function VitalChartFrame({
  className,
  icon,
  title,
  badgeLabel,
  badgeValue,
  legend = [],
  empty,
  children
}) {
  return (
    <section className={`vital-chart ${className}`}>
      <header className="vital-chart-header">
        <div className="vital-chart-title">
          <span className="vital-chart-icon">{icon}</span>
          <h2>{title}</h2>
        </div>
        <div className="vital-chart-badge">
          <span>{badgeLabel}</span>
          <strong>{badgeValue}</strong>
        </div>
      </header>
      {legend.length > 0 && (
        <div className="vital-legend">
          {legend.map((item) => (
            <span key={item.label}><i className={item.mark} /> {item.label}</span>
          ))}
        </div>
      )}
      {empty ? <p className="vital-empty">{empty}</p> : children}
    </section>
  )
}
